import browser from "webextension-polyfill";
import * as ewe from "@eyeo/webext-ad-filtering-solution";
import { noop, ytAllowlistStartDate, } from "./detection.types.js";
import { youTubeAlreadyAllowListed, youTubeAutoAllowlisted, youTubeWallDetected, youTubeNavigation, matchingElementFoundKey, ytHavenEmbeddedPlayerIssue, ytHavenMetaTagFound, ytHavenNextVideoNotFound, ytHavenNonEmbeddableFound, ytHavenNonPlayableFound, ytHavenToggleState, ytHavenDefaultEnabledFlag, ytHavenToggleStateUpdate, ytHavenToggleStatePrefsKey, ytHavenShouldStart, ytHavenEmbedFreeFlag, ytHavenEmbedPremiumFlag, ytHavenToggleStateEnabled, ytHavenToggleStateDisabled, ytHavenRecordEvent, ytHavenAutoAllowlistVideo, } from "../shared/index.js";
const captureStartDate = function (parameters) {
    if (parameters.prefs.get(ytAllowlistStartDate) === 0) {
        parameters.prefs.set(ytAllowlistStartDate, Date.now());
        parameters.logger.debug("[yt-detection]: set start date", new Date(parameters.prefs.get(ytAllowlistStartDate)).toLocaleDateString());
    }
};
const captureDateOnUpdate = (parameters, details) => {
    if (details.reason === "update" || details.reason === "install") {
        captureStartDate(parameters);
    }
};
const processYouTubeWallMessage = async (parameters, message, sender) => {
    var _a, _b;
    const tabId = (_a = sender.tab) === null || _a === void 0 ? void 0 : _a.id;
    const tabURL = (_b = sender.tab) === null || _b === void 0 ? void 0 : _b.url;
    if (typeof tabId === "undefined" || typeof tabURL === "undefined") {
        return;
    }
    const { sendAdWallEvents = noop } = parameters;
    const senderURL = new URL(tabURL);
    const host = senderURL.hostname.replace(/^www\./, "");
    const pathname = senderURL.pathname;
    const videoId = senderURL.searchParams.get("v");
    const isAllowlistVideo = await ewe.experiments.getFlag(ytHavenAutoAllowlistVideo);
    let allowlistUrl = host;
    if (isAllowlistVideo) {
        allowlistUrl = host + pathname;
        if (videoId) {
            allowlistUrl = `${allowlistUrl}?v=${videoId}`;
        }
    }
    const allowlistDuration = isAllowlistVideo ? "video" : "tab";
    const filters = await ewe.filters.getAllowingFilters(tabId);
    const isAllowListed = filters.some((f) => f.includes(allowlistUrl));
    const eventName = isAllowListed
        ? youTubeAlreadyAllowListed
        : youTubeWallDetected;
    sendAdWallEvents(eventName, {
        category: message.hasDismiss ? "soft" : "hard",
        userLoggedIn: message.userLoggedIn ? "1" : "0",
        isAllowListed: isAllowListed ? "1" : "0",
        allowlistDuration,
    });
    await browser.storage.local.set({
        [matchingElementFoundKey]: true,
    });
    if (isAllowListed) {
        return;
    }
    const ruleText = `@@||${allowlistUrl}^$document`;
    const metadata = {
        expiresByTabId: tabId,
        origin: "yt-auto",
        created: Date.now(),
    };
    await ewe.filters.add(ruleText, metadata);
    if (message.currentPlaybackTime > 5) {
        senderURL.searchParams.set("t", Math.floor(message.currentPlaybackTime).toString());
        senderURL.searchParams.delete("ab_channel");
        await browser.tabs.update(tabId, { url: senderURL.href });
    }
    else {
        await browser.tabs.reload(tabId);
    }
    sendAdWallEvents(youTubeAutoAllowlisted, {
        allowlistDuration,
    });
};
export function start(parameters) {
    const { sendAdWallEvents = noop } = parameters;
    const captureDateOnUpdatedWithParameters = captureDateOnUpdate.bind(null, parameters);
    browser.runtime.onInstalled.addListener(captureDateOnUpdatedWithParameters);
    ewe.filters.onRemoved.addListener(async (filter) => {
        if (filter.text.endsWith("youtube.com^$document") &&
            filter.type === "allowing") {
            await browser.storage.local.remove(matchingElementFoundKey);
        }
    });
    const processYouTubeWallMessageWithParameters = processYouTubeWallMessage.bind(null, parameters);
    parameters.port.on(youTubeWallDetected, processYouTubeWallMessageWithParameters);
    parameters.port.on(youTubeNavigation, async (message, sender) => {
        sendAdWallEvents(youTubeNavigation, {
            userLoggedIn: message.userLoggedIn ? "1" : "0",
        });
    });
    parameters.port.on(ytHavenNonEmbeddableFound, (message) => {
        sendAdWallEvents(ytHavenNonEmbeddableFound, {
            errorCode: message.errorCode,
        });
    });
    parameters.port.on(ytHavenNonPlayableFound, (message) => {
        sendAdWallEvents(ytHavenNonPlayableFound, {
            errorCode: message.errorCode,
        });
    });
    parameters.port.on(ytHavenNextVideoNotFound, () => {
        sendAdWallEvents(ytHavenNextVideoNotFound);
    });
    parameters.port.on(ytHavenMetaTagFound, () => {
        sendAdWallEvents(ytHavenMetaTagFound);
    });
    parameters.port.on(ytHavenEmbeddedPlayerIssue, (message) => {
        sendAdWallEvents(ytHavenEmbeddedPlayerIssue, {
            errorCode: message.errorCode,
        });
    });
    parameters.port.on(ytHavenRecordEvent, async ({ eventName, eventPayload }, sender) => {
        var _a;
        const tabId = (_a = sender.tab) === null || _a === void 0 ? void 0 : _a.id;
        let isAllowListed = false;
        if (typeof tabId !== "undefined") {
            const filters = await ewe.filters.getAllowingFilters(tabId);
            isAllowListed = filters.some((f) => f.includes("youtube.com"));
        }
        const isPremium = await parameters.isPremiumActive();
        sendAdWallEvents(eventName, {
            ...eventPayload,
            p: isPremium,
            isAllowListed: isAllowListed ? "1" : "0",
        });
    });
    parameters.port.on(ytHavenShouldStart, async () => {
        const isPremium = await parameters.isPremiumActive();
        const ytHavenFlags = await Promise.all([
            ewe.experiments.getFlag(ytHavenEmbedFreeFlag),
            ewe.experiments.getFlag(ytHavenEmbedPremiumFlag),
        ]);
        return ((Boolean(ytHavenFlags[0]) && !isPremium) ||
            (Boolean(ytHavenFlags[1]) && isPremium));
    });
    parameters.port.on(ytHavenToggleState, async () => {
        var _a;
        const isPremium = await parameters.isPremiumActive();
        const [currentState, isDefaultEnabled] = await Promise.all([
            browser.storage.local.get(ytHavenToggleStatePrefsKey),
            ewe.experiments.getFlag(ytHavenDefaultEnabledFlag),
        ]);
        return Boolean((_a = currentState[ytHavenToggleStatePrefsKey]) !== null && _a !== void 0 ? _a : (isPremium ? false : isDefaultEnabled));
    });
    parameters.port.on(ytHavenToggleStateUpdate, async ({ newState }) => {
        await browser.storage.local.set({
            [ytHavenToggleStatePrefsKey]: newState,
        });
        const isPremium = await parameters.isPremiumActive();
        const eventName = newState
            ? ytHavenToggleStateEnabled
            : ytHavenToggleStateDisabled;
        const eventPayload = { p: isPremium };
        sendAdWallEvents(eventName, eventPayload);
        return newState;
    });
    const trustedEventNames = [
        youTubeWallDetected,
        youTubeNavigation,
        ytHavenShouldStart,
        ytHavenNonEmbeddableFound,
        ytHavenNonPlayableFound,
        ytHavenEmbeddedPlayerIssue,
        ytHavenMetaTagFound,
        ytHavenNextVideoNotFound,
        ytHavenToggleState,
        ytHavenToggleStateUpdate,
        ytHavenRecordEvent,
    ];
    parameters.addTrustedMessageTypes("https://youtube.com", trustedEventNames);
    parameters.addTrustedMessageTypes("https://www.youtube.com", trustedEventNames);
    parameters.addTrustedMessageTypes("https://youtube-nocookie.com", trustedEventNames);
    parameters.addTrustedMessageTypes("https://www.youtube-nocookie.com", trustedEventNames);
}
